import { useState } from "react";
import { useTranslation } from "@privacy/translations";
import type { User } from "../api/users.types";
import * as FormDialog from "./FormDialog";
import { UserSelect } from "./UserSelect";

export const pickResponsibleId = (
  pickedId: string | null | undefined,
  candidates: Array<User> | undefined,
  defaultId: string | undefined
) =>
  pickedId === null
    ? null
    : pickedId && candidates?.some(candidate => candidate.userId === pickedId)
      ? pickedId
      : (defaultId ?? null);

export const useResponsiblePick = (
  tenantIds: Array<string>,
  candidatesQuery: { data?: { users: Array<User>; defaultResponsibleId?: string } },
  { useDefault }: { useDefault: boolean }
) => {
  const [pickedId, setPickedId] = useState<string | null | undefined>(undefined);
  const candidates = tenantIds.length === 0 ? [] : candidatesQuery.data?.users;
  return {
    candidates,
    responsibleId: pickResponsibleId(
      pickedId,
      candidates,
      useDefault ? candidatesQuery.data?.defaultResponsibleId : undefined
    ),
    pick: (userId: string | undefined) => setPickedId(userId || null),
  };
};

export const ResponsibleField = ({
  id,
  tenantCount,
  candidates,
  responsibleId,
  onSelect,
}: {
  id: string;
  tenantCount: number;
  candidates: Array<User> | undefined;
  responsibleId: string | null;
  onSelect: (userId: string | undefined) => void;
}) => {
  const { t } = useTranslation();
  const hasNoCandidates = tenantCount > 0 && candidates?.length === 0;

  return (
    <FormDialog.Field
      errorMessage={
        hasNoCandidates
          ? t(
              tenantCount === 1
                ? "RegistrationDrawer.Responsible.NoCandidates.Singular"
                : "RegistrationDrawer.Responsible.NoCandidates.Plural"
            )
          : undefined
      }
    >
      <FormDialog.Label htmlFor={id}>{t("RegistrationGrid.ResponsibleHeader")}</FormDialog.Label>
      <UserSelect
        id={id}
        selectedId={responsibleId}
        users={candidates}
        error={hasNoCandidates}
        onSelectUser={onSelect}
      />
    </FormDialog.Field>
  );
};
